import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useYardTaskFetcher } from '@satellite/composables/useYardTaskFetcher';
import { useYardTaskPresenter } from '@satellite/composables/useYardTaskPresenter';
import useYard from '@/modules/yard/composables/useYard';

import type { TaskViewData } from '@nova/core';
import type { ComputedRef, Ref } from 'vue';

export type UseYardTaskParams = {
  taskId?: Ref<string | null>;
  // initialTask is used only for initial state
  initialTask?:
    | TaskViewData
    | Ref<TaskViewData | null | undefined>
    | ComputedRef<TaskViewData | null | undefined>
    | null;
};

export type UseYardTaskOptions = {
  lazy?: boolean;
  live?: boolean;
};

export function useYardTask(
  { taskId, initialTask }: UseYardTaskParams = {},
  options: UseYardTaskOptions = {}
) {
  const { lazy, live } = { lazy: false, live: false, ...options };

  const yardStore = useYard();
  const { t } = useI18n();

  const warehouseTimezone = computed(() => {
    return yardStore.selectedWarehouseRef?.value?.timezone;
  });

  const fetcher = useYardTaskFetcher({ taskId, initialTask, lazy, live }, { warehouseTimezone });

  const presenter = useYardTaskPresenter(fetcher.task, {
    warehouseTimezone,
    i18n: t
  });

  return {
    // Fetching state
    isLoading: fetcher.isLoading,
    refresh: fetcher?.refresh,

    // Tasks
    rawTask: fetcher.rawTask,
    task: fetcher.task, // View-ready version from fetcher

    // All presenter properties (including events from ViewData)
    ...presenter,

    // Custom formatters
    formatDueDate: (dueDate?: string | null) => {
      const formatted = presenter.formatDate(dueDate || null);
      return formatted || t('yard.pages.yardTasks.messages.noDueDate');
    },
    formatCreatedAt: (createdAt?: string | null) => {
      const formatted = presenter.formatDate(createdAt || null);
      return formatted || '--';
    }
  };
}
